<template>
  <div>
    <form class="email-form" @submit.prevent="requestValidationCode">
      <b-field>
        <b-input type="email"
                 icon="at"
                 placeholder="You email address"
                 v-model="email">
        </b-input>
      </b-field>

      <template v-if="showResetButton">
        <button class="button is-primary is-medium is-fullwidth"
                :class="{ 'is-loading': isSaving }"
                :disabled="!canDoReset"
                type="submit">
          Reset Password
        </button>
        <button @click="toggleVerificationCodeField"
                class="button is-text help has-text-centered is-fullwidth has-background-white"
                type="button">
          I already have a verification code
        </button>
      </template>

      <b-notification class="error-message"
                      type="is-danger"
                      v-show="showError">
        There was a problem resetting your password. Please check your email address and try again.
      </b-notification>

      <b-notification class="error-message"
                      type="is-info"
                      :closable="false"
                      v-show="showSuccess">
        Your password reset request was submitted. <br/>
        A verification email has been sent to the address provided above. <br/>
        Please follow the instructions in that email to complete the password reset process.
      </b-notification>
    </form>

    <form v-show="showNewPasswordInput" class="new-password-from" @submit.prevent="setNewPassword">
      <b-field>
        <b-input type="text"
                 icon="fingerprint"
                 placeholder="Verification code"
                 v-model="verificationCode">
        </b-input>
      </b-field>

      <b-field :type="{ 'is-danger': !passwordIsValid }">
        <b-input type="password"
                 icon="key"
                 placeholder="New password"
                 :password-reveal="true"
                 v-model="newPassword">
        </b-input>
      </b-field>
      <b-field :type="{ 'is-danger': !doesPasswordsMatch }">
        <b-input type="password"
                 icon=""
                 placeholder="Password Confirmation"
                 :password-reveal="true"
                 v-model="passwordConfirmation">
        </b-input>
      </b-field>

      <button class="button is-primary is-medium is-fullwidth"
              :class="{ 'is-loading': isSaving }"
              :disabled="!canSetNewPassword"
              type="submit">
        Set new password
      </button>
      <span class=" is-text help has-text-centered is-fullwidth has-background-white">
        You will be automatically logged in once your new password is set.
      </span>

      <b-notification class="error-message"
                      type="is-danger"
                      v-show="showNewPasswordError">
        {{ passwordResponseError }}
      </b-notification>

    </form>
  </div>
</template>

<script>
import LoginApi from '@/api-interface/login'
import isValidPassword from '@/helpers/passwordValidation'
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'FormForgotPassword',

  props: {
    presetValidationCode: {
      type: String,
      default: () => null
    }
  },

  data() {
    return {
      email: '',
      verificationCode: '',
      newPassword: '',
      passwordConfirmation: '',
      showResetButton: true,
      isSaving: false,
      showSuccess: false,
      showError: false,
      showNewPasswordInput: false,
      showNewPasswordError: false,
      passwordResponseError: ''
    }
  },

  created() {
    if (this.presetValidationCode) {
      this.verificationCode = this.presetValidationCode
      this.toggleVerificationCodeField()
    }
  },

  computed: {
    ...mapGetters('user', ['isCarrier']),

    canDoReset() {
      return this.email.length > 1 && !this.showSuccess && !this.isSaving
    },

    canSetNewPassword() {
      return this.verificationCode.length > 1 && this.passwordIsValid && this.doesPasswordsMatch
    },

    passwordIsValid() {
      return isValidPassword(this.newPassword)
    },

    /**
     * Flag indicating if the confirmation password and password match
     * @returns {boolean}
     */
    doesPasswordsMatch() {
      return this.newPassword === this.passwordConfirmation
    }
  },

  methods: {
    ...mapActions('user', ['authenticate', 'getDetails']),

    toggleVerificationCodeField() {
      this.showNewPasswordInput = true
      this.showResetButton = false
    },

    /**
     * Starts the password reset flow by sending a validation code to the users email
     * @returns {Promise<void>}
     */
    async requestValidationCode() {
      this.isSaving = true
      this.showError = false

      try {
        await LoginApi.resetPasswordFlow(this.email)
        this.showSuccess = true
        this.showResetButton = false
      } catch (error) {
        this.showError = true
      } finally {
        this.isSaving = false
      }
    },

    /**
     * Sets the new user password
     * @returns {Promise<void>}
     */
    async setNewPassword() {
      this.isSaving = true
      this.showNewPasswordError = false

      try {
        await LoginApi.resetPasswordWithCode({
          email: this.email,
          code: this.verificationCode,
          password: this.newPassword
        })
        await this.authenticate({ username: this.email, password: this.newPassword })
        await this.getDetails()
        if (this.isCarrier) {
          window.alert(
            'Your password has been successfully changed. Please log in to the mobile app using your new password.'
          )
          window.location = '/'
        } else {
          this.$router.push({ name: 'dashboard' })
        }
      } catch (error) {
        this.passwordResponseError = `There was a problem resetting your password -- ${error.message}`
        this.showNewPasswordError = true
        this.isSaving = false
      }
    }
  }
}
</script>

<style scoped lang="scss">
.error-message {
  margin-top: 16px;
}

form {
  margin-bottom: 50px;
}
</style>
